import React, { useState, memo } from 'react';
import { FileText, Youtube, Lock, Calendar, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { usePrivacy } from '../../contexts/PrivacyContext';
import {
  calculateProgress,
  getProgressColor,
} from './kanbanUtils';

/**
 * KanbanCard — Video card for Kanban board
 *
 * Props:
 *   video: object
 *   isDragging: boolean
 *   columnColor: string
 *   onOpenScript: fn
 */
const KanbanCard = memo(
  ({ video, isDragging, columnColor, onChecklistChange, onOpenScript, onCardClick }) => {
    const { t } = useTranslation();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { isPrivate } = usePrivacy();

    const progress = calculateProgress(video);
    const progressColor = getProgressColor(progress);
    // Vinculado ao YouTube: thumb sincronizada vence a manual (igual ao modal)
    const thumbnailUrl = (video.youtube_video_id && video.youtube_thumbnail)
      ? video.youtube_thumbnail
      : (video.thumbnail_url || video.youtube_thumbnail || null);
    // Checklist items for mini display (field = DB column name for API updates)
    const checklistItems = [
      { key: 'T', field: 'checklist_titulo', title: t('kanban.check_title'), checked: !!(video.checklist_titulo || '').trim() },
      { key: 'R', field: 'checklist_roteiro', title: t('kanban.check_script'), checked: !!(video.checklist_roteiro || '').trim() },
      { key: 'A', field: 'checklist_audio', title: t('kanban.check_audio'), checked: !!(video.checklist_audio || '').trim() },
      { key: 'Th', field: 'checklist_thumbnail', title: t('kanban.check_thumbnail'), checked: !!(video.checklist_thumbnail || '').trim() },
      { key: 'Re', field: 'checklist_pronto', title: t('kanban.check_rendered'), checked: !!(video.checklist_pronto || '').trim() },
      { key: 'Pu', field: 'checklist_postagem', title: t('kanban.check_published'), checked: !!(video.checklist_postagem || '').trim() },
    ];

    return (
      <div
        onClick={() => onCardClick?.(video)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          position: 'relative',
          background: 'var(--dp-bg-elevated, rgba(15,23,42,0.8))',
          border: `1px solid ${
            isDragging
              ? 'var(--dp-blue-500, #0DAEFF)'
              : (() => {
                  // Check overdue inline (same logic as date display)
                  if (video.data_postagem && !(video.checklist_postagem || '').trim()) {
                    try {
                      const [d, m, y] = video.data_postagem.split('/');
                      const dv = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                      const h = new Date(); h.setHours(0,0,0,0); dv.setHours(0,0,0,0);
                      if (dv.getTime() < h.getTime()) return 'rgba(239, 68, 68, 0.5)';
                    } catch { /* ignore */ }
                  }
                  return 'var(--dp-border-subtle, rgba(71,85,105,0.3))';
                })()
          }`,
          borderRadius: '10px',
          overflow: 'hidden',
          cursor: video.is_imported ? 'default' : 'grab',
          transition: isDragging ? 'none' : 'all 0.2s ease',
          transform: isDragging
            ? 'scale(1.02)'
            : isHovered && !video.is_imported
            ? 'translateY(-2px)'
            : 'none',
          boxShadow: isDragging
            ? '0 12px 24px rgba(0, 0, 0, 0.4)'
            : isHovered
            ? '0 4px 12px rgba(0, 0, 0, 0.3)'
            : '0 1px 3px rgba(0, 0, 0, 0.1)',
          opacity: video.is_imported ? 0.85 : 1,
        }}
      >
        {/* YouTube badge */}
        {video.is_imported && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              padding: '3px 7px',
              background: 'rgba(255, 0, 0, 0.9)',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
              color: 'white',
              zIndex: 10,
              fontFamily: 'var(--dp-font)',
            }}
          >
            <Youtube size={10} />
            <span>YouTube</span>
            <Lock size={9} style={{ marginLeft: '2px' }} />
          </div>
        )}

        {/* Thumbnail area */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16/9',
            background: 'var(--dp-bg-card, rgba(30,41,59,0.5))',
            overflow: 'hidden',
          }}
        >
          {isPrivate ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(30, 41, 59, 0.8)',
                borderRadius: '0',
              }}
            >
              <Video
                size={28}
                style={{ color: 'var(--dp-text-tertiary, #4B5563)' }}
              />
            </div>
          ) : thumbnailUrl && !imageError ? (
            <>
              {/* Shimmer while loading */}
              {!imageLoaded && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(90deg, rgba(30,41,59,0.5) 0%, rgba(51,65,85,0.5) 50%, rgba(30,41,59,0.5) 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'kanban-shimmer 1.5s infinite',
                  }}
                />
              )}
              <img
                src={thumbnailUrl}
                alt={video.titulo || 'Thumbnail'}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                referrerPolicy="no-referrer"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              />
            </>
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--dp-bg-card, rgba(30,41,59,0.8))',
              }}
            >
              <FileText
                size={28}
                style={{ color: 'var(--dp-text-tertiary, #4B5563)' }}
              />
            </div>
          )}

        </div>

        {/* Card content */}
        <div style={{ padding: '10px 12px' }}>
          {/* Title */}
          <h4
            style={{
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--dp-text-primary, #F5F5F5)',
              lineHeight: 1.35,
              margin: '0 0 6px 0',
              fontFamily: 'var(--dp-font)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {isPrivate ? '••••••••••' : (video.titulo || t('kanban.untitled'))}
          </h4>

          {/* Date */}
          {/* Date — same logic as list view (CanalManagerNew) */}
          {video.data_postagem && (() => {
            try {
              const [day, month, year] = video.data_postagem.split('/');
              const dataVideo = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              const hoje = new Date();
              hoje.setHours(0, 0, 0, 0);
              dataVideo.setHours(0, 0, 0, 0);
              const isPostado = (video.checklist_postagem || '').trim() !== '';
              const isDataPassada = dataVideo.getTime() < hoje.getTime();
              const isDataFutura = dataVideo.getTime() > hoje.getTime();
              let cor, texto;
              if (!isPostado && isDataPassada) {
                cor = '#EF4444';
                texto = `${t('kanban.overdue')} ${video.data_postagem}`;
              } else if (!isPostado) {
                cor = 'var(--dp-blue-500)';
                texto = `${t('kanban.publish_on')} ${video.data_postagem}`;
              } else if (isDataFutura) {
                cor = '#F59E0B';
                texto = `${t('kanban.scheduled_for')} ${video.data_postagem}`;
              } else {
                cor = '#10B981';
                texto = video.data_postagem;
              }
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  fontSize: '10px', color: cor, marginBottom: '8px',
                  fontFamily: 'var(--dp-font)',
                }}>
                  <Calendar size={10} />
                  {texto}
                </div>
              );
            } catch {
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  fontSize: '10px', color: 'var(--dp-blue-500)', marginBottom: '8px',
                  fontFamily: 'var(--dp-font)',
                }}>
                  <Calendar size={10} />
                  {video.data_postagem}
                </div>
              );
            }
          })()}

          {/* Progress bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                flex: 1,
                height: '3px',
                background: 'var(--dp-border-subtle, rgba(71,85,105,0.3))',
                borderRadius: '2px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${progress}%`,
                  borderRadius: '2px',
                  background: progressColor,
                  transition: 'width 0.3s ease, background-color 0.3s ease',
                }}
              />
            </div>
            <span
              style={{
                fontSize: '10px',
                fontWeight: 500,
                color: 'var(--dp-text-tertiary, #9CA3AF)',
                minWidth: '28px',
                textAlign: 'right',
                fontFamily: 'var(--dp-font)',
              }}
            >
              {progress}%
            </span>
          </div>

          {/* Mini checklist — clickable to toggle */}
          <div style={{ display: 'flex', gap: '3px' }}>
            {checklistItems.map((item) => (
              <div
                key={item.key}
                title={item.title}
                onClick={(e) => {
                  e.stopPropagation();
                  if (video.is_imported) return;
                  onChecklistChange?.(video.id, item.field, !item.checked);
                }}
                style={{
                  width: '22px',
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  fontSize: item.checked ? '10px' : '8px',
                  fontWeight: 600,
                  fontFamily: 'var(--dp-font)',
                  cursor: video.is_imported ? 'default' : 'pointer',
                  background: item.checked
                    ? 'rgba(16, 185, 129, 0.2)'
                    : 'var(--dp-border-subtle, rgba(71,85,105,0.15))',
                  border: `1px solid ${
                    item.checked
                      ? 'rgba(16, 185, 129, 0.4)'
                      : 'var(--dp-border-subtle, rgba(71,85,105,0.2))'
                  }`,
                  color: item.checked
                    ? '#10B981'
                    : 'var(--dp-text-tertiary, #6B7280)',
                  transition: 'all 0.2s ease',
                }}
              >
                {item.checked ? '\u{2713}' : item.key}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 12px 8px 12px',
            borderTop:
              '1px solid var(--dp-border-subtle, rgba(71,85,105,0.15))',
          }}
        >
          {/* Video number */}
          <span
            style={{
              fontSize: '10px',
              fontWeight: 500,
              color: 'var(--dp-text-tertiary, #6B7280)',
              fontFamily: 'var(--dp-font)',
              flexShrink: 0,
            }}
          >
            #{video.numero_video || '?'}
          </span>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {!video.is_imported && onOpenScript && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenScript(video);
                }}
                title={t('kanban.view_edit_script')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '3px 8px',
                  background: 'transparent',
                  border: '1px solid var(--dp-border-subtle, rgba(71,85,105,0.25))',
                  borderRadius: '6px',
                  color: 'var(--dp-text-tertiary, #9CA3AF)',
                  fontSize: '10px',
                  fontWeight: 500,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--dp-border-subtle, rgba(71,85,105,0.2))';
                  e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)';
                  e.currentTarget.style.color = 'var(--dp-blue-500, #0DAEFF)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'var(--dp-border-subtle, rgba(71,85,105,0.25))';
                  e.currentTarget.style.color = 'var(--dp-text-tertiary, #9CA3AF)';
                }}
              >
                <FileText size={11} />
                {t('kanban.script')}
              </button>
            )}

            {/* Copy Notes button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isPrivate) { toast.info(t('kanban.privacy_mode_active')); return; }
                navigator.clipboard.writeText(video.notas || '');
                toast.success(t('kanban.description_copied'));
              }}
              title={t('kanban.copy_description')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 8px',
                background: 'transparent',
                border: '1px solid var(--dp-border-subtle, rgba(71,85,105,0.25))',
                borderRadius: '6px',
                color: 'var(--dp-text-tertiary, #9CA3AF)',
                fontSize: '10px',
                fontWeight: 500,
                fontFamily: 'var(--dp-font)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--dp-border-subtle, rgba(71,85,105,0.2))';
                e.currentTarget.style.borderColor = 'var(--dp-border-medium, rgba(71,85,105,0.4))';
                e.currentTarget.style.color = 'var(--dp-text-primary, #F5F5F5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'var(--dp-border-subtle, rgba(71,85,105,0.25))';
                e.currentTarget.style.color = 'var(--dp-text-tertiary, #9CA3AF)';
              }}
            >
              {'\u{1F4CB}'} {t('kanban.description')}
            </button>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.video.id === nextProps.video.id &&
      prevProps.video.updated_at === nextProps.video.updated_at &&
      prevProps.video.titulo === nextProps.video.titulo &&
      prevProps.video.data_postagem === nextProps.video.data_postagem &&
      prevProps.video.thumbnail === nextProps.video.thumbnail &&
      prevProps.video.status === nextProps.video.status &&
      prevProps.video.checklist_postagem === nextProps.video.checklist_postagem &&
      prevProps.video.checklist_pronto === nextProps.video.checklist_pronto &&
      prevProps.video.checklist_roteiro === nextProps.video.checklist_roteiro &&
      prevProps.video.checklist_titulo === nextProps.video.checklist_titulo &&
      prevProps.video.checklist_audio === nextProps.video.checklist_audio &&
      prevProps.video.checklist_thumbnail === nextProps.video.checklist_thumbnail &&
      prevProps.video.notas === nextProps.video.notas &&
      prevProps.video.numero_video === nextProps.video.numero_video &&
      prevProps.isDragging === nextProps.isDragging
    );
  }
);

KanbanCard.displayName = 'KanbanCard';

export default KanbanCard;
